<template>
  <div
    class="tw-flex tw-gap-x-2"
    :class="{
      'tw-flex-row-reverse tw-justify-end': props.labelBeforeToggle,
    }"
  >
    <button
      class="ds-toggle"
      :style="cssVars"
      :class="{
        'ds-toggle--checked': modelValue,
        'ds-toggle--disabled': disabled,
      }"
      @click="toggleValue"
    >
      <div
        class="ds-toggle__switch"
        :class="{
          'ds-toggle__switch--checked': modelValue,
        }"
      ></div>
    </button>
    <span
      v-if="hasLabel"
      class="ds-toggle__label"
      :class="{
        'tw-cursor-not-allowed tw-text-gray-medium-dark': disabled,
      }"
      @click="toggleValue"
    >
      <slot name="label">{{ label }}</slot>
    </span>
  </div>
</template>

<script setup lang="ts">
import { computed, useSlots } from 'vue';
import { Colors } from '@/api';

interface Props {
  modelValue: boolean;
  label?: string;
  primaryColor?: Colors;
  secondaryColor?: Colors | 'gray-medium';
  disabled?: boolean;
  /** [toggle - label] when false, [label - toggle] when true */
  labelBeforeToggle?: boolean;
}
interface Emits {
  (e: 'update:modelValue', value: boolean): void;
}

const props = withDefaults(defineProps<Props>(), {
  primaryColor: Colors.BLUE,
  secondaryColor: 'gray-medium',
  disabled: false,
});
const emit = defineEmits<Emits>();

const slots = useSlots() as Record<string, unknown>;
const hasLabel = computed(() => props.label || slots.label);

const cssVars = computed(() => {
  // Use CSS variables directly - no need for expensive useThemedColors()
  return {
    '--ds-toggle-primary-color': `rgb(var(--tw-color-${props.primaryColor}))`,
    '--ds-toggle-secondary-color': `rgb(var(--tw-color-${props.secondaryColor}))`,
  };
});

function toggleValue() {
  if (props.disabled) return;
  emit('update:modelValue', !props.modelValue);
}
</script>

<style lang="scss" scoped>
.ds-toggle {
  width: 28px;
  height: 16px;
  border-radius: 8px;
  padding: 0 2px;
  @apply tw-flex tw-items-center tw-transition-all tw-relative tw-cursor-pointer;
  background-color: var(--ds-toggle-secondary-color);

  &:focus {
    outline: 1px solid var(--ds-toggle-primary-color) !important;
    outline-offset: 1px !important;
  }

  &--checked {
    background-color: var(--ds-toggle-primary-color);
    @apply tw-transition-all tw-duration-300;
  }

  &--disabled {
    @apply tw-cursor-not-allowed tw-opacity-70;

    & + .ds-toggle__label {
      @apply tw-cursor-not-allowed tw-text-gray-medium-dark;
    }
  }

  &__switch {
    width: 12px;
    height: 12px;
    left: 2px;
    top: 2px;
    @apply tw-h-3 tw-w-3 tw-rounded-full tw-transition-all tw-duration-300 tw-bg-white tw-absolute;

    &--checked {
      left: 14px;
      @apply tw-transition-all tw-duration-300;
    }
  }
}
.ds-toggle__label {
  @apply tw-text-xs tw-text-gray-dark tw-cursor-pointer;
}
</style>
